// ConsolidadoExistenciasListado.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { RefreshCw, FileSpreadsheet } from "lucide-react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Select } from "./components/ui/select";
import { Switch } from "./components/ui/switch";
import { Badge } from "./components/ui/badge";
import { DataTable } from "./components/ui/data-table";
import { Tooltip } from "./components/ui/tooltip";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

const token = "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

const EP_TIPOS = `${apiUrl}/ServiceFamilyGrowers.asmx/FamilyGetTipoProductos`;
const EP_LISTA = `${apiUrl}/ServiceFamilyGrowers.asmx/FamilyGetConsolidadoExistencias`;

const parseResponse = (data) => {
  try {
    if (data && typeof data === "object") {
      if ("d" in data) return typeof data.d === "string" ? JSON.parse(data.d) : data.d;
      return data;
    }
    return JSON.parse(String(data ?? ""));
  } catch { return null; }
};

const fmt2 = (n) => (Number(n) || 0).toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUSD = (n) => (Number(n) || 0).toLocaleString("es-CL", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

export default function ConsolidadoExistenciasListado() {
  const [loading, setLoading] = useState(false);
  const [tipos, setTipos] = useState([{ value: 0, label: "Todos" }]);
  const [idTipoProducto, setIdTipoProducto] = useState(0);
  const [rows, setRows] = useState([]);
  const [hideZeroSaldo, setHideZeroSaldo] = useState(false);

  const loadTipos = useCallback(async () => {
    try {
      const res = await axios.get(EP_TIPOS, { headers: { Authorization: token } });
      const payload = parseResponse(res.data);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setTipos([{ value: 0, label: "Todos" }].concat(list.map((x) => ({ value: Number(x.idtipoproducto || x.id || 0), label: String(x.nombre || "").trim() }))));
    } catch (e) { console.error(e); toast.error("No se pudieron cargar los tipos de producto."); }
  }, []);

  const loadConsolidado = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(EP_LISTA, { headers: { Authorization: token }, params: { idTipoProducto: Number(idTipoProducto || 0) } });
      const payload = parseResponse(res.data);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      const norm = list.map((r, i) => ({
        key: `${r.producto || ""}-${i}`,
        producto: String(r.producto || "").trim(),
        totalEntrada: Number(r.totalEntrada) || 0,
        totalSalida: Number(r.totalSalida) || 0,
        saldoFinal: Number(r.saldoFinal) || 0,
        totalDolares: Number(r.totalDolares) || 0,
        fechasVcto: String(r.fechasVcto || ""),
      }));
      setRows(norm);
    } catch (e) { console.error(e); toast.error("No se pudo cargar el consolidado de existencias."); }
    finally { setLoading(false); }
  }, [idTipoProducto]);

  useEffect(() => { loadTipos(); loadConsolidado(); }, [loadTipos, loadConsolidado]);

  const visibleRows = useMemo(() => {
    if (!hideZeroSaldo) return rows;
    return rows.filter((r) => Number(r.saldoFinal) !== 0);
  }, [rows, hideZeroSaldo]);

  const totals = useMemo(() => visibleRows.reduce(
    (acc, r) => { acc.e += r.totalEntrada; acc.s += r.totalSalida; acc.sf += r.saldoFinal; acc.usd += r.totalDolares; return acc; },
    { e: 0, s: 0, sf: 0, usd: 0 }
  ), [visibleRows]);

  const columns = useMemo(() => [
    { title: "Producto", dataIndex: "producto", width: 240, ellipsis: true },
    { title: "Entradas", dataIndex: "totalEntrada", align: "right", width: 120, render: (v) => fmt2(v), sorter: (a, b) => a.totalEntrada - b.totalEntrada },
    { title: "Salidas", dataIndex: "totalSalida", align: "right", width: 120, render: (v) => fmt2(v), sorter: (a, b) => a.totalSalida - b.totalSalida },
    {
      title: "Saldo", dataIndex: "saldoFinal", align: "right", width: 120,
      render: (v) => (
        <span className={v > 0 ? "text-blue-600 font-medium" : v < 0 ? "text-red-600 font-medium" : ""}>
          {fmt2(v)}
        </span>
      ),
      sorter: (a, b) => a.saldoFinal - b.saldoFinal,
    },
    { title: "Total US$", dataIndex: "totalDolares", align: "right", width: 140, render: (v) => fmtUSD(v), sorter: (a, b) => a.totalDolares - b.totalDolares },
    {
      title: "F. Vcto", dataIndex: "fechasVcto", width: 260,
      render: (v) => {
        const arr = String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
        if (!arr.length) return "—";
        return (
          <div className="flex flex-wrap gap-1">
            {arr.map((d, i) => <Badge key={`${d}-${i}`} variant="outline">{d}</Badge>)}
          </div>
        );
      },
    },
  ], []);

  const handleExportExcel = useCallback(async () => {
    try {
      const XLSX = await import("xlsx");
      const data = visibleRows.map((r) => ({ Producto: r.producto, Entradas: Number(r.totalEntrada || 0), Salidas: Number(r.totalSalida || 0), Saldo: Number(r.saldoFinal || 0), "Total US$": Number(r.totalDolares || 0), "F. Vcto": r.fechasVcto }));
      const ws = XLSX.utils.json_to_sheet(data);
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, ...data.map((row) => (row[h] ? String(row[h]).length : 0))) + 2 }));
      XLSX.utils.sheet_add_json(ws, [{ Producto: "TOTAL", Entradas: totals.e, Salidas: totals.s, Saldo: totals.sf, "Total US$": totals.usd, "F. Vcto": "" }], { skipHeader: true, origin: -1 });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Consolidado");
      XLSX.writeFile(wb, `ConsolidadoExistencias_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { console.error(e); toast.error("No se pudo exportar a Excel."); }
  }, [visibleRows, totals]);

  const summaryRow = useCallback(() => (
    <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold text-sm">
      <td className="px-3 py-2">TOTAL</td>
      <td className="px-3 py-2 text-right">{fmt2(totals.e)}</td>
      <td className="px-3 py-2 text-right">{fmt2(totals.s)}</td>
      <td className="px-3 py-2 text-right">{fmt2(totals.sf)}</td>
      <td className="px-3 py-2 text-right">{fmtUSD(totals.usd)}</td>
      <td className="px-3 py-2" />
    </tr>
  ), [totals]);

  return (
    <div className="bg-white p-4 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 m-0">Consolidado de Existencias</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            options={tipos}
            value={idTipoProducto}
            onChange={(v) => setIdTipoProducto(Number(v) || 0)}
            style={{ width: 280 }}
            placeholder="Tipo de producto"
            showSearch
            optionFilterProp="label"
          />
          <Tooltip title="Si está activo, no se listan productos con saldo = 0">
            <div className="flex items-center gap-2 cursor-default">
              <Switch checked={hideZeroSaldo} onCheckedChange={setHideZeroSaldo} />
              <span className="text-sm text-gray-600">Ocultar saldo = 0</span>
            </div>
          </Tooltip>
          <Tooltip title="Consultar consolidado">
            <Button onClick={loadConsolidado} loading={loading}>
              <RefreshCw className="h-4 w-4" /> Generar
            </Button>
          </Tooltip>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </Button>
        </div>
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={visibleRows}
        rowKey="key"
        bordered
        scroll={{ x: 1100 }}
        pagination={{
          current: 1,
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100, 200],
          showTotal: (total, [start, end]) => `${start}-${end} de ${total} registros`,
        }}
        onPaginationChange={() => {}}
        summary={summaryRow}
      />
    </div>
  );
}
